import { useRef, useLayoutEffect, useCallback } from "react";

// Shrinks a single-line heading's font-size until it fits its container width,
// so long names stay on one row without ever overflowing (no side-scroll).
export default function useFitOneLine(text, { max = 60, min = 14, reserve = 0 } = {}) {
    const ref = useRef(null);
    const fit = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        // When the heading sits in a FLEX ROW (e.g. beside a verified badge) it becomes a
        // shrink-to-content flex item, so its own clientWidth is just the text width — no
        // good for fitting. Measure the parent row's content width instead (minus its padding
        // and `reserve` for the badge). For a block or flex-COLUMN heading, the element
        // already stretches to the available width, so its own clientWidth is correct.
        const parent = el.parentElement;
        const pcs = parent ? getComputedStyle(parent) : null;
        const parentIsFlexRow = pcs && /flex/.test(pcs.display) && !/column/.test(pcs.flexDirection);
        let base;
        if (parentIsFlexRow) {
            const padX = (parseFloat(pcs.paddingLeft) || 0) + (parseFloat(pcs.paddingRight) || 0);
            base = (parent.clientWidth || 0) - padX;
        } else {
            base = el.clientWidth || (parent && parent.clientWidth) || 0;
        }
        const avail = base - reserve;
        if (!avail || avail <= 0) return;
        let s = max;
        el.style.fontSize = `${s}px`;
        while (s > min && el.scrollWidth > avail) {
            s -= 1;
            el.style.fontSize = `${s}px`;
        }
    }, [max, min, reserve]);
    useLayoutEffect(() => {
        fit();
        let cancelled = false;
        const ro = new ResizeObserver(fit);
        if (ref.current?.parentElement) ro.observe(ref.current.parentElement);
        if (document.fonts?.ready) document.fonts.ready.then(() => { if (!cancelled) fit(); });
        window.addEventListener("resize", fit);
        return () => { cancelled = true; ro.disconnect(); window.removeEventListener("resize", fit); };
    }, [fit, text]);
    return ref;
}
